'use client';

import { c as _c } from "react/compiler-runtime";
import { buttonVariants } from '@achra/ui/button';
import { Card, CardContent } from '@achra/ui/card';
import { cn } from '@achra/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@achra/ui/tooltip';
import { cva } from 'class-variance-authority';
import Link from 'next/link';
import { useCallback } from 'react';
import type { WorkstreamDetailsProject } from '@/modules/project/types';
import { OverflowList } from '@/modules/shared/components/overflow-list';
import { getTagVariant } from '@/modules/workstream/lib/proposal-apply-card-helpers';
import type { Route } from 'next';
interface ProposalApplyCardProps {
  project?: WorkstreamDetailsProject;
  title: string;
  description: string;
  tags: string[];
  networkSlug: string;
  workstreamSlug: string;
}
const tagVariants = cva('inline-flex items-center px-4 py-0 rounded-md text-sm font-semibold whitespace-nowrap border-2 text-foreground/50', {
  variants: {
    variant: {
      default: 'border-muted',
      yellow: 'border-[rgba(250,196,0,0.30)]',
      success: 'border-status-success/30',
      progress: 'border-status-progress/30',
      destructive: 'border-destructive/30',
      purple: 'border-purple/30',
      warning: 'border-status-warning/30'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
function ProposalApplyCard(t0) {
  const $ = _c(16);
  const {
    networkSlug,
    workstreamSlug,
    title,
    description,
    tags,
    project
  } = t0;
  const projectSlug = project?.slug ?? "";
  const href = projectSlug ? `/network/${networkSlug}/workstream/${workstreamSlug}/initial-proposal/${projectSlug}/project-details` as Route : "#" as Route;
  const itemRenderer = _temp;
  const overflowRenderer = _temp3;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="mb-0.5 line-clamp-2 text-sm/5.5 font-semibold sm:truncate xl:text-base/6">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = <div className="line-clamp-2 text-sm/5.5 md:line-clamp-3">{description}</div>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tags) {
    t3 = <div className="mt-3 flex flex-wrap gap-2 sm:mt-4"><OverflowList items={tags} itemRenderer={itemRenderer} overflowRenderer={overflowRenderer} /></div>;
    $[4] = tags;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="w-full max-w-[calc(100%-86px)]">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className={cn(buttonVariants())}>Apply</span>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4) {
    t6 = <Card className="cursor-pointer rounded-lg p-0 shadow-sm transition-all hover:shadow-md"><CardContent className="flex gap-4 p-2 sm:p-3">{t4}{t5}</CardContent></Card>;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== href || $[14] !== t6) {
    t7 = <Link href={href} className="block">{t6}</Link>;
    $[13] = href;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function _temp3(items) {
  return <Tooltip><TooltipTrigger asChild={true}><div className={cn(tagVariants({
        variant: "default"
      }), "px-2", "select-none")}>+{items.length}</div></TooltipTrigger><TooltipContent side="bottom" align="start"><div className="max-w-64 space-y-1"><div className="flex flex-wrap gap-2">{items.map(_temp2)}</div></div></TooltipContent></Tooltip>;
}
function _temp2(tag_0) {
  return <div key={tag_0} className={cn(tagVariants({
    variant: getTagVariant(tag_0)
  }), "text-xs")}>{tag_0}</div>;
}
function _temp(tag) {
  return <div className={tagVariants({
    variant: getTagVariant(tag)
  })}>{tag}</div>;
}
export { ProposalApplyCard };